/**
 * Campaign attribution capture.
 * Publishers link with: https://natyax.co.in/checkout?id=1&clickid=ABC123
 *
 * id=1 → monthly (Digipedia postback on conversion)
 * id=2 → monthly (no postback — internal campaigns)
 *
 * clickid is captured on first touch and kept in localStorage so it
 * survives the checkout → pay → return funnel, then sent to the server
 * at subscribe time. Analytics only — never a session token.
 */

const CLICKID_KEY = 'nx_clickid';
const PACK_KEY    = 'nx_pack';
const VISITOR_KEY = 'nx_visitor';

function safeGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
}

/** Call once on app mount. Reads campaign params and persists them. First-touch wins for clickid. */
export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const click  = params.get('clickid') ?? params.get('Clickid');
    if (click && !safeGet(CLICKID_KEY)) safeSet(CLICKID_KEY, click.slice(0, 128));
    const pack = params.get('id');
    if (pack && /^\d+$/.test(pack)) safeSet(PACK_KEY, pack);
  } catch { /* no-op */ }
}

export function getClickId(): string | null {
  return safeGet(CLICKID_KEY);
}

export function getPackId(): string | null {
  return safeGet(PACK_KEY);
}

/**
 * Stable per-browser visitor id, minted on first use.
 * Used only to dedupe checkout-hit beacons across refreshes.
 * Not a session token; absent in private mode.
 */
export function getVisitorId(): string | null {
  let id = safeGet(VISITOR_KEY);
  if (!id) {
    try { id = crypto.randomUUID(); }
    catch { id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; }
    safeSet(VISITOR_KEY, id);
  }
  return id;
}

/** Pack-to-plan map. */
export const PACK_TO_PLAN: Record<string, 'weekly' | 'monthly'> = {
  '1': 'weekly',  // PayU — ₹99/week — Digipedia postback
  '2': 'monthly', // PayU — ₹199/month — Digipedia postback
  '3': 'monthly', // SabPaisa — ₹199/month — Digipedia postback
};

/** id=3 uses SabPaisa; everything else uses PayU (default). */
export const PACK_TO_GATEWAY: Record<string, 'payu' | 'sabpaisa'> = {
  '3': 'sabpaisa',
};

/** All packs send postback to Digipedia. */
export const NO_POSTBACK_PACKS = new Set<string>();
